import React, { useEffect, useRef, useState } from 'react';

function WinnerShareCard({ winner, cycleId, onClose }) {
  const canvasRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [copied, setCopied] = useState({ link: false, text: false });

  const shareUrl = `https://app.thesubgames.com/winner/${cycleId}`;
  const shareText = `I just won the SubGames!\n\nThanks to all ${winner.supporterCount || 0} of my supporters!\n\nWin the games, Win the views:\nThe SubGames`;

  useEffect(() => {
    generateWinCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateWinCard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = 1200;
    const height = 630; // Standard social media size
    
    canvas.width = width;
    canvas.height = height;

    // Gradient background
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#8B5CF6'); // Purple
    gradient.addColorStop(0.5, '#EC4899'); // Pink
    gradient.addColorStop(1, '#F59E0B'); // Amber
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Add some decorative circles
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.beginPath();
    ctx.arc(200, 100, 150, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(1000, 500, 200, 0, Math.PI * 2);
    ctx.fill();

    // Main content area
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(50, 50, width - 100, height - 100);

    // Trophy emoji
    ctx.font = 'bold 120px Arial';
    ctx.fillStyle = '#FFD700';
    ctx.textAlign = 'center';
    ctx.fillText('🏆', width / 2, 180);

    // "I WON!" text
    ctx.font = 'bold 90px Arial';
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 10;
    ctx.fillText('I WON!', width / 2, 280);

    // Winner name
    ctx.font = 'bold 60px Arial';
    ctx.fillStyle = '#FFD700';
    const displayName = winner.winnerName || 'Anonymous';
    ctx.fillText(displayName, width / 2, 360);

    // Stats
    ctx.font = '36px Arial';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(`${winner.finalScore || 0} Points  •  ${winner.supporterCount || 0} Supporters`, width / 2, 420);

    // SubGames branding
    ctx.font = 'bold 40px Arial';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText('SubGames', width / 2, 520);

    // URL
    ctx.font = '28px Arial';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.fillText('app.thesubgames.com', width / 2, 560);

    ctx.shadowBlur = 0;

    // Try to load and draw profile picture
    if (winner.winnerPhotoURL) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          // Draw circular profile picture
          ctx.save();
          ctx.beginPath();
          ctx.arc(150, 150, 70, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, 80, 80, 140, 140);
          ctx.restore();
          
          // Border around profile pic
          ctx.strokeStyle = '#FFD700';
          ctx.lineWidth = 6;
          ctx.beginPath();
          ctx.arc(150, 150, 70, 0, Math.PI * 2);
          ctx.stroke();

          // Convert to image URL
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            setImageUrl(url);
          }, 'image/png');
        };
        img.onerror = () => {
          // If image fails to load, just use the card without profile pic
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            setImageUrl(url);
          }, 'image/png');
        };
        img.src = winner.winnerPhotoURL;
      } catch (error) {
        console.error('Error loading profile image:', error);
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          setImageUrl(url);
        }, 'image/png');
      }
    } else {
      // No profile picture, convert canvas to image
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
      }, 'image/png');
    }
  };

  const handleDownload = () => {
    if (!imageUrl) return;
    
    const link = document.createElement('a');
    link.download = `SubGames-Win-${cycleId}.png`;
    link.href = imageUrl;
    link.click();
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [type]: true });
      setTimeout(() => {
        setCopied({ ...copied, [type]: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900 rounded-2xl p-6 max-w-2xl w-full border-4 border-yellow-400/50 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="float-right text-white/70 hover:text-white text-3xl font-bold"
        >
          ×
        </button>
        
        <div className="text-center mb-6">
          <h2 className="text-4xl font-bold text-yellow-300 mb-2">You Won!</h2>
          <p className="text-white/90 text-lg">Share your victory with your community!</p>
        </div>

        {/* Canvas for image generation (hidden) */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Preview of the generated image */}
        {imageUrl && (
          <div className="mb-6 bg-white/10 rounded-xl p-4">
            <p className="text-white font-semibold mb-3 text-center">Your Win Card:</p>
            <img 
              src={imageUrl} 
              alt="Win Card" 
              className="w-full rounded-lg shadow-xl border-2 border-white/20"
            />
          </div>
        )}

        {/* Share Options */}
        <div className="space-y-3">
          {/* Download Win Card */}
          <button
            onClick={handleDownload}
            disabled={!imageUrl}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            Download Win Card
          </button>

          {/* Copy Share Link */}
          <button
            onClick={() => copyToClipboard(shareUrl, 'link')}
            className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-3 px-6 rounded-xl transition-all border border-white/30 flex items-center justify-center gap-2"
          >
            {copied.link ? '✓ Link Copied!' : 'Copy Share Link'}
          </button>

          {/* Copy Share Text */}
          <button
            onClick={() => copyToClipboard(shareText, 'text')}
            className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-3 px-6 rounded-xl transition-all border border-white/30 flex items-center justify-center gap-2"
          >
            {copied.text ? '✓ Text Copied!' : 'Copy Share Message'}
          </button>

          {/* Share Text Preview */}
          <div className="bg-white/10 rounded-xl p-4 border border-white/20">
            <p className="text-white text-sm whitespace-pre-line font-mono">{shareText}</p>
          </div>

          {/* Visit Channel Button */}
          {winner.promotionalURL && (
            <button
              onClick={() => window.open(winner.promotionalURL, '_blank')}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-xl transition-all shadow-lg"
            >
              Visit Your Channel
            </button>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-3 px-6 rounded-xl transition-all border border-white/20"
          >
            Close
          </button>
        </div>

        {/* Tips */}
        <div className="mt-6 bg-white/10 rounded-xl p-4 border border-white/20">
          <p className="text-white/90 text-sm text-center mb-2">
            <strong>Share Tips:</strong>
          </p>
          <ul className="text-white/70 text-xs space-y-1">
            <li>• Download the win card for Instagram Stories/TikTok</li>
            <li>• Copy the share link for Twitter/Discord</li>
            <li>• Use the share message on any platform</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default WinnerShareCard;
